import { WorkflowState } from '@compass/client/types/case_record';
import { type Evaluation, EvaluationStatus } from '@compass/client/types/evaluation';
import { evaluationService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

import { QC_EVAL_DETAIL_QUERY_CONFIG } from '@/constants/queryConfig';

const FAST_WORKFLOW_POLL_INTERVAL_MS = 2000;
const DEFAULT_WORKFLOW_POLL_INTERVAL_MS = 5000;
const SLOW_WORKFLOW_POLL_INTERVAL_MS = 15000;
const WORKFLOW_POLL_TIMEOUT_MS = 15 * 60 * 1000;

const hasReviewerFindingReviews = (evaluation?: Evaluation): boolean =>
  !!evaluation?.reviews?.findings && Object.keys(evaluation.reviews.findings).length > 0;

const shouldPollReviewerReevaluation = (evaluation?: Evaluation): boolean => {
  const workflowState = evaluation?.workflow_state;
  if (workflowState !== WorkflowState.WORKFLOW_STATE_RUNNING) {
    return false;
  }

  return (
    evaluation?.status === EvaluationStatus.EVALUATION_STATUS_COMPLETED &&
    hasReviewerFindingReviews(evaluation)
  );
};

const getWorkflowPollStartedAt = (evaluation?: Evaluation): number =>
  evaluation?.updated_at ? Date.parse(evaluation.updated_at) : Number.NaN;

const getWorkflowPollInterval = (elapsedMs: number): number => {
  if (elapsedMs < 60 * 1000) {
    return FAST_WORKFLOW_POLL_INTERVAL_MS;
  }
  if (elapsedMs < 5 * 60 * 1000) {
    return DEFAULT_WORKFLOW_POLL_INTERVAL_MS;
  }
  return SLOW_WORKFLOW_POLL_INTERVAL_MS;
};

export const useEvaluation = (
  evaluationId: string | null | undefined,
  organizationIdOverride?: string | null,
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const effectiveOrganizationId = organizationIdOverride || selectedOrganizationId;

  return useQuery({
    queryKey: [...queryKeys.evaluations.byId(evaluationId || ''), effectiveOrganizationId] as const,
    queryFn: () =>
      evaluationService.getEvaluation({
        organization_id: effectiveOrganizationId!,
        evaluation_id: evaluationId!,
        // AQ-653: the QC list (and its "only ineligible" view) links to ineligible
        // evaluations, but GetEvaluation excludes ineligible by default and 404s.
        // Always opt in so opening any listed row resolves.
        ineligible: true,
      }),
    enabled: !!effectiveOrganizationId && !!evaluationId,
    refetchInterval: query => {
      const evaluation = query.state.data?.evaluation;
      if (!shouldPollReviewerReevaluation(evaluation)) {
        return false;
      }

      const startedAt = getWorkflowPollStartedAt(evaluation);
      if (!Number.isNaN(startedAt) && Date.now() - startedAt > WORKFLOW_POLL_TIMEOUT_MS) {
        return false;
      }

      return Number.isNaN(startedAt)
        ? DEFAULT_WORKFLOW_POLL_INTERVAL_MS
        : getWorkflowPollInterval(Date.now() - startedAt);
    },
    ...QC_EVAL_DETAIL_QUERY_CONFIG,
  });
};
